/**
 * Circuit calculations (copper conductors, AC 50 Hz), in the simple form used to check
 * residential and small tertiary installations:
 * - design current Ib from the connected loads (or the protection rating when loads are unknown);
 * - current-carrying capacity Iz per IEC 60364-5-52 (tables B.52.2–B.52.5, ambient 30 °C, one
 *   circuit: no grouping factor) and the coordination Ib ≤ In ≤ Iz;
 * - voltage drop ΔU = b·(ρ·L/S·cosφ + λ·L·sinφ)·Ib, b = 2 single-phase, √3 three-phase,
 *   ρ = 0.0225 Ω·mm²/m (copper at service temperature), λ = 0.08 mΩ/m;
 * - minimum short-circuit current at the end of the cable by the conventional method
 *   (0.8·U0 at the origin of the circuit, resistance at 1.25 × 20 °C) compared with the
 *   magnetic trip threshold (B 5·In, C 10·In, D 20·In), which gives the maximum protected length.
 * These are design checks, not a substitute for the measurements of the inspection.
 */
import type { Id, Project } from '@elec/domain';
import {
  analyse,
  boardCircuits,
  boardPhases,
  circuitPoles,
  PHASES,
  type Analysis,
  type CircuitInfo,
  type Phase,
} from '@elec/analysis';

export const RHO = 0.0225; // Ω·mm²/m, copper, for voltage drop and conventional short-circuit
export const LAMBDA = 0.00008; // Ω/m
export const U0 = 230;
export const U3 = 400;

/** Installation methods we tabulate (IEC 60364-5-52 reference methods). */
export const INSTALL_METHODS = ['A1', 'B1', 'C'] as const;
export type InstallMethod = (typeof INSTALL_METHODS)[number];
export const INSTALL_METHOD_LABEL: Record<InstallMethod, { fr: string; en: string; nl: string }> = {
  A1: {
    fr: 'A1 · sous conduit dans paroi isolante',
    en: 'A1 · conduit in thermally insulated wall',
    nl: 'A1 · buis in thermisch geïsoleerde wand',
  },
  B1: {
    fr: 'B1 · sous conduit (encastré ou apparent)',
    en: 'B1 · in conduit (in or on a wall)',
    nl: 'B1 · in buis (ingebouwd of opbouw)',
  },
  C: {
    fr: 'C · câble fixé au mur',
    en: 'C · cable clipped to a wall',
    nl: 'C · kabel op een wand bevestigd',
  },
};

const SECTIONS = [1.5, 2.5, 4, 6, 10, 16, 25];
// Iz (A), copper. [method][insulation][loaded conductors] → values per SECTIONS.
const IZ: Record<InstallMethod, Record<'PVC' | 'XLPE', Record<2 | 3, number[]>>> = {
  A1: {
    PVC: { 2: [14.5, 19.5, 26, 34, 46, 61, 80], 3: [13.5, 18, 24, 31, 42, 56, 73] },
    XLPE: { 2: [19, 26, 35, 45, 61, 81, 106], 3: [17, 23, 31, 40, 54, 73, 95] },
  },
  B1: {
    PVC: { 2: [17.5, 24, 32, 41, 57, 76, 101], 3: [15.5, 21, 28, 36, 50, 68, 89] },
    XLPE: { 2: [23, 31, 42, 54, 75, 100, 133], 3: [20, 28, 37, 48, 66, 88, 117] },
  },
  C: {
    PVC: { 2: [19.5, 27, 36, 46, 63, 85, 112], 3: [17.5, 24, 32, 41, 57, 76, 96] },
    XLPE: { 2: [24, 33, 45, 58, 80, 107, 138], 3: [22, 30, 40, 52, 71, 96, 119] },
  },
};

/** Insulation of Belgian cable types: X… = cross-linked polyethylene (90 °C), V… = PVC (70 °C). */
export function insulationOf(cableType: string): 'PVC' | 'XLPE' {
  return /^(E?X)/i.test(cableType.trim()) ? 'XLPE' : 'PVC';
}

export function currentCapacity(
  sectionMm2: number,
  method: InstallMethod,
  insulation: 'PVC' | 'XLPE',
  loaded: 2 | 3,
): number | undefined {
  const i = SECTIONS.indexOf(sectionMm2);
  return i < 0 ? undefined : IZ[method][insulation][loaded][i];
}

const MAG: Record<string, number> = { B: 5, C: 10, D: 20 };

export interface CircuitCalc {
  code: string;
  threePhase: boolean;
  /** Connected load (kW), when known. */
  loadKw: number | undefined;
  /** Design current (A) and where it comes from. */
  ib: number | undefined;
  ibSource: 'loads' | 'protection' | 'none';
  inA: number | undefined;
  method: InstallMethod;
  iz: number | undefined;
  /** Ib ≤ In ≤ Iz. undefined when something is missing. */
  coordinationOk: boolean | undefined;
  lengthM: number | undefined;
  /** Voltage drop of the circuit, % of the nominal voltage. */
  dropPct: number | undefined;
  /** Limit used for this circuit: 3 % lighting, 5 % other uses. */
  dropLimitPct: number;
  /** Minimum short-circuit current at the end (A). */
  iccMinA: number | undefined;
  /** Magnetic trip threshold (A). */
  imA: number | undefined;
  /** Maximum length protected against short circuits (m). */
  lMaxM: number | undefined;
  /** Breaking capacity of the device (kA), if given. */
  breakingKa: number | undefined;
}

const num = (v: unknown) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v
    : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v.replace(',', '.')))
      ? Number(v.replace(',', '.'))
      : undefined;

export function isThreePhase(c: CircuitInfo): boolean {
  const poles = num(c.device.props.poles);
  return (
    (poles !== undefined && poles >= 3) ||
    /breaker-[34]p|fuse-3p/.test(c.device.symbolId) ||
    c.cable.cores === 5
  );
}

const COS_PHI = 1; // cookers, water heaters, heaters, chargers: resistive or power-factor corrected
const COS_PHI_MOTOR = 0.8;

/**
 * Line voltage of the supply: 400 V for 3N×400 V, 230 V for the Belgian 3×230 V networks
 * (no neutral: single-phase circuits are connected between two phases, still at 230 V).
 */
export const lineVoltage = (supplyVoltage: string) => (/400/.test(supplyVoltage) ? U3 : U0);

export function circuitCalc(c: CircuitInfo, uLine = U3): CircuitCalc {
  const threePhase = isThreePhase(c);
  const inA = c.ratingA;
  let loadKw = 0;
  let known = false;
  let motor = false;
  for (const p of c.points) {
    const kw = num(p.element.props.powerKw);
    if (kw !== undefined) {
      loadKw += kw * (num(p.element.props.count) ?? 1);
      known = true;
    }
    if (p.element.symbolId.includes('motor') || p.element.symbolId.includes('heat-pump')) motor = true;
  }
  // Unknown loads (sockets, lighting) are checked at In with a typical cos φ of 0.95.
  const cos = motor ? COS_PHI_MOTOR : known ? COS_PHI : 0.95;
  const sin = Math.sqrt(1 - cos * cos);
  // Sockets and lighting have no fixed load: check the circuit at its protection rating.
  const onlyKnown = known && c.points.every((p) => num(p.element.props.powerKw) !== undefined);
  const ib = onlyKnown ? (loadKw * 1000) / (threePhase ? Math.sqrt(3) * uLine * cos : U0 * cos) : inA;
  const ibSource: CircuitCalc['ibSource'] = onlyKnown ? 'loads' : inA !== undefined ? 'protection' : 'none';

  const method = (INSTALL_METHODS as readonly string[]).includes(c.circuit?.cable.installMethod ?? '')
    ? (c.circuit!.cable.installMethod as InstallMethod)
    : 'B1';
  const S = c.cable.sectionMm2;
  const iz = S > 0 ? currentCapacity(S, method, insulationOf(c.cable.type), threePhase ? 3 : 2) : undefined;
  const coordinationOk =
    ib !== undefined && inA !== undefined && iz !== undefined
      ? ib <= inA + 1e-9 && inA <= iz + 1e-9
      : undefined;

  const L = c.circuit?.cable.lengthM;
  const lighting = c.points.length > 0 && c.points.every((p) => p.kind === 'light');
  const dropLimitPct = lighting ? 3 : 5;
  let dropPct: number | undefined;
  if (L && S > 0 && ib !== undefined) {
    const b = threePhase ? Math.sqrt(3) : 2;
    const du = b * ((RHO * L * cos) / S + LAMBDA * L * sin) * ib;
    dropPct = (100 * du) / (threePhase ? uLine : U0);
  }

  const curve = String(c.device.props.curve ?? (c.device.symbolId.includes('fuse') ? '' : 'C'));
  const imA = inA !== undefined && MAG[curve] ? MAG[curve]! * inA : undefined;
  // Phase–neutral loop (same section both ways). Three-phase: phase–neutral too (worst case for 3N).
  const iccMinA = L && S > 0 ? (0.8 * U0 * S) / (RHO * 2 * L) : undefined;
  const lMaxM = imA && S > 0 ? (0.8 * U0 * S) / (RHO * 2 * imA) : undefined;

  return {
    code: c.code,
    threePhase,
    loadKw: known ? loadKw : undefined,
    ib,
    ibSource,
    inA,
    method,
    iz,
    coordinationOk,
    lengthM: L,
    dropPct,
    dropLimitPct,
    iccMinA,
    imA,
    lMaxM,
    breakingKa: num(c.device.props.breakingKa),
  };
}

const cache = new WeakMap<Analysis, Map<string, CircuitCalc>>();

/** Calculations for every circuit, keyed by protective device id. */
export function projectCalcs(
  project: Project,
  analysis: Analysis = analyse(project),
): Map<string, CircuitCalc> {
  const hit = cache.get(analysis);
  if (hit) return hit;
  const u = lineVoltage(project.meta.supplyVoltage);
  const m = new Map(analysis.circuits.map((c) => [c.device.id, circuitCalc(c, u)]));
  cache.set(analysis, m);
  return m;
}

export interface PhaseLoads {
  three: boolean;
  /** Design current from the stated loads on each phase (A); circuits with unknown loads count 0. */
  amps: Record<Phase, number>;
  /** Circuit codes (or device tags) on each phase. */
  circuits: Record<Phase, string[]>;
  /** Circuits whose load is unknown (sockets, lighting without a power). */
  unknown: string[];
}

/**
 * Load per phase of a three-phase board, from the circuits' stated powers (Ib from the loads):
 * a single-phase circuit on its phase (boardPhases), a three-phase one on all three.
 */
export function phaseLoads(
  project: Project,
  analysis: Analysis,
  fromSheetId: Id,
  /** Phases to assume instead of the drawn ones (what balancedPhases proposes). */
  override?: ReadonlyMap<Id, Phase>,
): PhaseLoads {
  const { three, of: drawn } = boardPhases(project, analysis, fromSheetId);
  const of = new Map(drawn);
  for (const [id, p] of override ?? []) if (of.has(id)) of.set(id, [p]);
  const calcs = projectCalcs(project, analysis);
  const amps: Record<Phase, number> = { L1: 0, L2: 0, L3: 0 };
  const circuits: Record<Phase, string[]> = { L1: [], L2: [], L3: [] };
  const unknown: string[] = [];
  for (const c of boardCircuits(analysis, fromSheetId)) {
    const k = calcs.get(c.device.id);
    const phases = (of.get(c.device.id) ?? []).filter((p): p is Phase =>
      (PHASES as readonly string[]).includes(p),
    );
    const label = c.code || c.device.tag;
    for (const p of phases) circuits[p].push(label);
    if (k?.ibSource !== 'loads' || k.ib === undefined) {
      if (!c.circuit?.spare) unknown.push(label);
      continue;
    }
    for (const p of phases) amps[p] += k.ib;
  }
  return { three, amps, circuits, unknown };
}

/**
 * Unbalance of a three-phase board: the most and least loaded phases, and whether it is too much.
 * (R-CALC-06 also asks that moving circuits would help: see `improvableUnbalance`.)
 */
export function phaseUnbalance(l: PhaseLoads): {
  max: Phase;
  min: Phase;
  deltaA: number;
  unbalanced: boolean;
} {
  const sorted = [...PHASES].sort((a, b) => l.amps[b] - l.amps[a]);
  const max = sorted[0]!;
  const min = sorted[2]!;
  const deltaA = l.amps[max] - l.amps[min];
  // ⚠ to confirm: 10 A or more between phases and the least loaded below 70 % of the most.
  return { max, min, deltaA, unbalanced: l.three && deltaA >= 10 && l.amps[min] < 0.7 * l.amps[max] };
}

/**
 * Phases that balance a three-phase board: the single-phase circuits with the biggest stated
 * loads first, each on the least loaded phase (three-phase loads count on all three), then the
 * circuits with no stated load spread in turn. Only the circuits whose phase changes are
 * returned (by device id); empty on a single-phase board.
 */
export function balancedPhases(project: Project, analysis: Analysis, fromSheetId: Id): Map<Id, Phase> {
  const { three, of } = boardPhases(project, analysis, fromSheetId);
  const out = new Map<Id, Phase>();
  if (!three) return out;
  const calcs = projectCalcs(project, analysis);
  const load: Record<Phase, number> = { L1: 0, L2: 0, L3: 0 };
  const single: Array<{ id: Id; ib: number }> = [];
  for (const c of boardCircuits(analysis, fromSheetId)) {
    const k = calcs.get(c.device.id);
    const ib = k?.ibSource === 'loads' ? (k.ib ?? 0) : 0;
    if (circuitPoles(c) > 2) for (const p of PHASES) load[p] += ib;
    else single.push({ id: c.device.id, ib });
  }
  const least = () =>
    [...PHASES].sort((x, y) => load[x] - load[y] || PHASES.indexOf(x) - PHASES.indexOf(y))[0]!;
  const want = new Map<Id, Phase>();
  for (const { id, ib } of single.filter((x) => x.ib > 0).sort((x, y) => y.ib - x.ib)) {
    const p = least();
    want.set(id, p);
    load[p] += ib;
  }
  let i = 0;
  for (const { id } of single.filter((x) => x.ib === 0)) want.set(id, PHASES[i++ % 3]!);
  for (const [id, p] of want) if (of.get(id)?.[0] !== p) out.set(id, p);
  return out;
}

/**
 * The board is unbalanced and moving single-phase circuits would reduce the gap between phases by
 * at least 5 A. A board whose imbalance comes from one big load that cannot be split (a cooker
 * alone on its phase) is not reported: only feeding that load in three-phase would help.
 */
export function improvableUnbalance(
  project: Project,
  analysis: Analysis,
  fromSheetId: Id,
): { loads: PhaseLoads; deltaA: number; bestDeltaA: number } | null {
  const loads = phaseLoads(project, analysis, fromSheetId);
  const u = phaseUnbalance(loads);
  if (!u.unbalanced) return null;
  const moves = balancedPhases(project, analysis, fromSheetId);
  const best = phaseUnbalance(phaseLoads(project, analysis, fromSheetId, moves));
  return u.deltaA - best.deltaA >= 5 ? { loads, deltaA: u.deltaA, bestDeltaA: best.deltaA } : null;
}
export * from './energy.ts';
